import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import {
    Text,
    Button,
    SearchBox,
    Spinner,
    mergeClasses,
} from '@fluentui/react-components';
import {
    ArrowSortDownRegular,
    ArrowSortUpRegular,
    ArrowLeftRegular,
    ArrowRightRegular,
    SearchRegular,
    FolderOpenRegular,
} from '@fluentui/react-icons';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { usePolicyBrowserStyles } from './PolicyBrowser.styles';
import type { PolicyApiClient } from '../../services/api/policyApiClient';
import { withImpersonation } from '../../utils/impersonation';
import type { PolicyArtifact } from '../../types/policy';

interface PolicyBrowserProps {
    apiClient: PolicyApiClient;
}

type SortDirection = 'asc' | 'desc';

const sortLabels: Record<SortDirection, string> = {
    asc: 'Title (A-Z)',
    desc: 'Title (Z-A)',
};

/** Minimal projection of a policy kept in the "viewed" list. */
interface RecentPolicy {
    id: string;
    title: string;
    category: string;
    contentType: 'html' | 'pdf';
}

const RECENTLY_VIEWED_KEY = 'hrPolicies.recentlyViewed';
const MAX_RECENTLY_VIEWED = 6;

/** Minimum row height plus its divider - keep in sync with `row` in PolicyBrowser.styles.ts. */
const ROW_HEIGHT = 81;
/** Card header + pagination bar + breathing room below the card. */
const CARD_CHROME_HEIGHT = 140;
/** Heading + search box above the card, used only for the first render before we can measure. */
const ESTIMATED_CARD_TOP = 220;
const MIN_PAGE_SIZE = 3;
const MAX_PAGE_SIZE = 24;

function computePageSize(cardTop: number): number {
    const rows = Math.floor((window.innerHeight - cardTop - CARD_CHROME_HEIGHT) / ROW_HEIGHT);
    return Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, rows));
}

function readRecentlyViewed(): RecentPolicy[] {
    try {
        const raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed
            .filter(
                (entry): entry is RecentPolicy =>
                    !!entry &&
                    typeof (entry as RecentPolicy).id === 'string' &&
                    typeof (entry as RecentPolicy).title === 'string'
            )
            .slice(0, MAX_RECENTLY_VIEWED);
    } catch {
        return [];
    }
}

function matchesSearch(policy: PolicyArtifact, normalizedQuery: string): boolean {
    return (
        policy.title.toLowerCase().includes(normalizedQuery) ||
        (policy.description ?? '').toLowerCase().includes(normalizedQuery) ||
        (policy.tags ?? []).some((tag) => tag.toLowerCase().includes(normalizedQuery))
    );
}

/** Delay before announcing results, so rapid typing produces one announcement instead of one per keystroke. */
export const SEARCH_ANNOUNCEMENT_DELAY_MS = 300;

export function formatSearchOutcome(rawQuery: string, count: number): string {
    const countMessage = count === 1 ? '1 policy found' : `${count} policies found`;
    const query = rawQuery.trim();
    if (!query) {
        const prefix = rawQuery === '' ? 'Search cleared. ' : '';
        return count === 0 ? `${prefix}No policies found.` : `${prefix}${countMessage}.`;
    }
    return count === 0
        ? `No policies found for “${query}”. Try adjusting your search.`
        : `${countMessage} for “${query}”.`;
}

export const PolicyBrowser: React.FC<PolicyBrowserProps> = ({ apiClient }) => {
    const styles = usePolicyBrowserStyles();
    const { search } = useLocation();

    const [allPolicies, setAllPolicies] = useState<PolicyArtifact[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [searchText, setSearchText] = useState<string>('');
    const [statusMessage, setStatusMessage] = useState('');
    const hasSearched = useRef(false);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [page, setPage] = useState(1);
    const [recentlyViewed, setRecentlyViewed] = useState<RecentPolicy[]>(() => readRecentlyViewed());
    const [pageSize, setPageSize] = useState(() => computePageSize(ESTIMATED_CARD_TOP));
    const listCardRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        const measure = () => {
            const top = listCardRef.current?.getBoundingClientRect().top ?? ESTIMATED_CARD_TOP;
            setPageSize(computePageSize(top));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, []);

    // Load the user's full policy set once; search, sort, and paging then run locally with no
    // further API calls. Company code and audience are resolved by the API from the signed-in user.
    const fetchPolicies = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setAllPolicies(await apiClient.getAllPolicies());
        } catch (err) {
            setError('Failed to load policies. Please try again.');
            console.error('Policy search error:', err);
        } finally {
            setLoading(false);
        }
    }, [apiClient]);

    useEffect(() => {
        fetchPolicies();
    }, [fetchPolicies]);

    // Reset to the first page whenever the visible result set is reshaped
    useEffect(() => {
        setPage(1);
    }, [searchText, sortDirection, pageSize]);

    const filteredItems = useMemo(() => {
        const normalizedQuery = searchText.trim().toLowerCase();
        return normalizedQuery
            ? allPolicies.filter((policy) => matchesSearch(policy, normalizedQuery))
            : allPolicies;
    }, [allPolicies, searchText]);

    const sortedItems = useMemo(() => {
        const direction = sortDirection === 'asc' ? 1 : -1;
        return [...filteredItems].sort((a, b) => direction * a.title.localeCompare(b.title));
    }, [filteredItems, sortDirection]);

    // Announce the total match count (not just the current page) once typing settles. Nothing is
    // announced until the user searches, or while the list is loading or failed to load.
    const matchCount = filteredItems.length;
    useEffect(() => {
        if (!hasSearched.current || loading || error) {
            return;
        }
        const timer = window.setTimeout(
            () => setStatusMessage(formatSearchOutcome(searchText, matchCount)),
            SEARCH_ANNOUNCEMENT_DELAY_MS
        );
        return () => window.clearTimeout(timer);
    }, [searchText, matchCount, loading, error]);

    const handleSearchChange = useCallback((value: string) => {
        hasSearched.current = true;
        // Clear first so a repeat of the same message is still a change the live region reports.
        setStatusMessage('');
        setSearchText(value);
    }, []);

    const sortButtonRef = useRef<HTMLButtonElement | null>(null);
    const refocusSortButton = useRef(false);

    const toggleSortDirection = useCallback(() => {
        refocusSortButton.current = true;
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    }, []);

    // The sort button is re-mounted per direction (see its `key`); move focus to the new one
    // before paint so keyboard and screen reader users stay on the control they activated.
    useLayoutEffect(() => {
        if (refocusSortButton.current) {
            refocusSortButton.current = false;
            sortButtonRef.current?.focus();
        }
    }, [sortDirection]);

    const totalPages = Math.max(1, Math.ceil(sortedItems.length / pageSize));
    const currentPage = Math.min(page, totalPages);

    const pageNavigated = useRef(false);
    const previousButtonRef = useRef<HTMLButtonElement | null>(null);
    const nextButtonRef = useRef<HTMLButtonElement | null>(null);
    const goToPage = useCallback((nextPage: number) => {
        pageNavigated.current = true;
        setStatusMessage('');
        setPage(nextPage);
    }, []);

    // Reaching the first or last page disables the button that was just pressed, which drops focus
    // to <body>. Hand focus to the opposite button (always enabled then) before paint instead.
    useLayoutEffect(() => {
        if (!pageNavigated.current) {
            return;
        }
        if (currentPage <= 1 && document.activeElement !== nextButtonRef.current) {
            nextButtonRef.current?.focus();
        } else if (currentPage >= totalPages && document.activeElement !== previousButtonRef.current) {
            previousButtonRef.current?.focus();
        }
    }, [currentPage, totalPages]);

    // Announce the position only after Previous/Next, from the page actually displayed, so search,
    // sort, and resize (which also reset paging) don't add a second announcement.
    useEffect(() => {
        if (pageNavigated.current) {
            pageNavigated.current = false;
            setStatusMessage(`Page ${currentPage} of ${totalPages}`);
        }
    }, [currentPage, totalPages]);
    const pagedItems = useMemo(
        () => sortedItems.slice((currentPage - 1) * pageSize, currentPage * pageSize),
        [sortedItems, currentPage, pageSize]
    );

    // Persist synchronously rather than inside a state updater: opening a policy navigates to a
    // sibling route that unmounts this component, so a queued updater would never run.
    const rememberPolicy = useCallback((artifact: RecentPolicy) => {
        const next = [
            {
                id: artifact.id,
                title: artifact.title,
                category: artifact.category,
                contentType: artifact.contentType,
            },
            ...recentlyViewed.filter((entry) => entry.id !== artifact.id),
        ].slice(0, MAX_RECENTLY_VIEWED);
        try {
            window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(next));
        } catch {
            // Storage unavailable (private mode / quota) - keep recents in memory only.
        }
        setRecentlyViewed(next);
    }, [recentlyViewed]);

    const policyPath = useCallback(
        (artifact: RecentPolicy) => withImpersonation(`/policies/${encodeURIComponent(artifact.id)}`, search),
        [search]
    );

    const viewedIds = useMemo(
        () => new Set(recentlyViewed.map((entry) => entry.id)),
        [recentlyViewed]
    );

    const renderTypeBadge = (contentType: 'html' | 'pdf') => (
        <span
            className={mergeClasses(
                styles.typeBadge,
                contentType === 'pdf' ? styles.typeBadgePdf : styles.typeBadgeHtml
            )}
            aria-hidden="true"
        >
            {contentType.toUpperCase()}
        </span>
    );

    return (
        <div className={styles.container}>
            <h1 className={styles.pageHeading}>
                Benefits and policies{!loading && !error && ` (${allPolicies.length})`}
            </h1>

            <SearchBox
                className={styles.searchBox}
                placeholder="Search HR Policies"
                value={searchText}
                onChange={(_, data) => handleSearchChange(data.value)}
                contentBefore={<SearchRegular />}
                aria-label="Search HR Policies"
            />

            <section ref={listCardRef} className={styles.card} aria-label="Policy results">
                <div role="status" className={styles.visuallyHidden}>
                    {statusMessage}
                </div>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardHeading}>
                        All policies ({sortedItems.length})
                    </h2>
                    <div className={styles.sortControl}>
                        <Text size={200}>Sort by:</Text>
                        <Button
                            // A fresh element per direction: re-focusing it makes screen readers announce the
                            // full name *and* role, instead of a bare name-change on the same element.
                            key={sortDirection}
                            ref={sortButtonRef}
                            appearance="transparent"
                            size="small"
                            className={styles.sortButton}
                            icon={sortDirection === 'asc' ? <ArrowSortUpRegular /> : <ArrowSortDownRegular />}
                            iconPosition="after"
                            onClick={toggleSortDirection}
                            aria-label={`Sorted by ${sortLabels[sortDirection]}. Activate to sort by ${
                                sortLabels[sortDirection === 'asc' ? 'desc' : 'asc']
                            }.`}
                        >
                            {sortLabels[sortDirection]}
                        </Button>
                    </div>
                </div>

                {loading && (
                    <div className={styles.stateBlock}>
                        <Spinner size="large" label="Loading policies..." />
                    </div>
                )}

                {error && !loading && (
                    <div className={styles.stateBlock}>
                        <Text>{error}</Text>
                        <Button appearance="primary" onClick={fetchPolicies}>Retry</Button>
                    </div>
                )}

                {!loading && !error && sortedItems.length === 0 && (
                    <div className={styles.stateBlock}>
                        <FolderOpenRegular fontSize={40} aria-hidden="true" />
                        <Text weight="semibold">No policies found</Text>
                        <Text size={200}>Try adjusting your search.</Text>
                    </div>
                )}

                {!loading && !error && pagedItems.length > 0 && (
                    <ul className={styles.list}>
                        {pagedItems.map((artifact: PolicyArtifact) => {
                            const isViewed = viewedIds.has(artifact.id);
                            return (
                                <li key={artifact.id}>
                                    <RouterLink
                                        to={policyPath(artifact)}
                                        className={styles.row}
                                        onClick={() => rememberPolicy(artifact)}
                                        aria-label={`Open policy: ${artifact.title}. ${artifact.contentType.toUpperCase()}.${isViewed ? ' Viewed.' : ''}`}
                                    >
                                        {renderTypeBadge(artifact.contentType)}
                                        <span className={styles.rowText}>
                                            <span className={styles.rowTitle}>{artifact.title}</span>
                                            {isViewed && (
                                                <span className={styles.viewedChip}>Viewed</span>
                                            )}
                                        </span>
                                    </RouterLink>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {!loading && !error && totalPages > 1 && (
                    <div className={styles.pagination}>
                        <Button
                            ref={previousButtonRef}
                            appearance="subtle"
                            icon={<ArrowLeftRegular />}
                            disabled={currentPage <= 1}
                            onClick={() => goToPage(currentPage - 1)}
                        >
                            Previous
                        </Button>
                        <Text size={200}>
                            Page {currentPage} of {totalPages}
                        </Text>
                        <Button
                            ref={nextButtonRef}
                            appearance="subtle"
                            icon={<ArrowRightRegular />}
                            iconPosition="after"
                            disabled={currentPage >= totalPages}
                            onClick={() => goToPage(currentPage + 1)}
                        >
                            Next
                        </Button>
                    </div>
                )}
            </section>
        </div>
    );
};
